import React, { SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

import { StatusCode } from "api/protocols";

import { showError } from "redux/actions/ui";

import { LocaleCurrency } from "helpers/Locale";
import { displayCurrency } from "helpers/Helpers";
import { useAppDispatch, useAppSelector } from "hooks/redux";

import { useWindowSize } from "hooks/useWindowSize/useWindowSize";

import DataTable from "../../../../../../components/dataTable/DataTable";
import ReactDOM from "react-dom";
import { ICreditTransactionRes } from "api/rpc/2024-04/facilityAdmin/customer/customerCredit";
import { useGuestPortalContext } from "pages/guest/Portal/GuestPortalContext";
import { GetCreditBookTransactions, GetCustomerCredit } from "api/rpc/2024-04/customer/customer";
import { transactionBadge } from "../houseAccounts/PortalHouseAccounts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWallet } from "@fortawesome/pro-regular-svg-icons";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import { TABLET_WIDTH } from "helpers/ScreenSizes";
import { DBFilterContext } from "redux/reducers/filters/filters";
import { filterContextIsEqual } from "components/accordion/Accordion";
import CustomerFilterAccordion from "components/accordion/filterAccordions/CustomerFilterAccordion";
import { Select } from "components/select/";
import "./portalCredit.scss";

interface ICreditBookState {
  creditBooks: any[];
  selectedCreditBook: any;
  transactions: ICreditTransactionRes[];
}

export default function PortalCredit() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const windowSize = useWindowSize();
  const { portalState } = useGuestPortalContext();
  const guest = portalState.guest;
  const today = new Date();
  const { Option } = Select;
  const [creditState, setCreditState] = useState([null]);
  const [creditTransactionState, setCreditTransactionState] = useState<ICreditTransactionRes[]>([null]);
  const customer_id = guest?.id;

  const [creditBookState, updateCreditBookState] = useState<ICreditBookState>({
    creditBooks: [],
    selectedCreditBook: null,
    transactions: [],
  });

  const [activeFilters, setActiveFilters] = useState<DBFilterContext[]>(undefined);
  const filters = useAppSelector(store => store.billingPageFilters);

  const [filterState, setFilterState] = useState({
    all: undefined,
    date: [],
    financial_status: [],
    register_ids: [],
    user_ids: [],
    sales_channels: [],
    credit_card: [],
    payment_type: [],
    dateRangeStart: today,
    dateRangeEnd: today,
  });

  const [dateFilter, setDateFilter] = useState({
    start_date: "",
    end_date: "",
  });

  useEffect(() => {
    void loadCustomerCredit();
  }, []);

  useEffect(() => {
    void loadCreditTransactions(Number(creditBookState?.selectedCreditBook?.id));
  }, [creditBookState?.selectedCreditBook]);

  useEffect(() => {
    if (activeFilters === undefined) {
      setActiveFilters(filters);
    }

    if (creditState[0]) {
      void loadCreditTransactions(Number(creditState[0]?.id));
    }
  }, [activeFilters, dateFilter]);

  async function loadCustomerCredit() {
    const customerCreditRes = await GetCustomerCredit({ customer_id: customer_id }, true);
    if (Number(customerCreditRes.status) !== Number(StatusCode.OK)) {
      dispatch(showError(t("Error getting credit book")));
      return;
    }

    ReactDOM.unstable_batchedUpdates(() => {
      setCreditState(customerCreditRes.data as SetStateAction<any[]>);

      updateCreditBookState(prevState => ({
        ...prevState,
        creditBooks: customerCreditRes.data,
        selectedCreditBook: customerCreditRes.data[0],
      }));
    });
  }

  async function loadCreditTransactions(credit_book_id: number) {
    if (creditTransactionState) {
      setCreditTransactionState([]);
    }

    // remove unused filters from accordion
    let filterParams = activeFilters
      ?.filter(grouping => grouping.value.length !== 0)
      .map(ele => {
        return {
          label: ele.label,
          value: ele.value,
        };
      });

    // apply date filter if given
    if (dateFilter.start_date.length !== 0) {
      filterParams = filterParams.concat([
        { label: "start_date", value: [dateFilter.start_date] },
        { label: "end_date", value: [dateFilter.end_date] },
      ]);
    }

    const params = {
      filters: filterParams?.length > 0 ? filterParams : undefined,
      credit_book_id: credit_book_id,
    };

    const creditTransactionRes = await GetCreditBookTransactions(params, true);
    if (creditTransactionRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting credit book transactions")));
      return;
    }

    ReactDOM.unstable_batchedUpdates(() => {
      setCreditTransactionState(creditTransactionRes.data as ICreditTransactionRes[]);
      updateCreditBookState(prevState => ({ ...prevState, transactions: creditTransactionRes.data }));
    });
  }

  function setDateRangeStart(date: Date) {
    setDateFilter(prevState => ({ ...prevState, start_date: date.toISOString().split("T")[0] }));

    setFilterState(prevState => ({
      ...prevState,
      dateRangeStart: date,
    }));
  }

  function setDateRangeEnd(date: Date) {
    setDateFilter(prevState => ({ ...prevState, end_date: date.toISOString().split("T")[0] }));

    setFilterState(prevState => ({
      ...prevState,
      dateRangeEnd: date,
    }));
  }

  function handleCreditBookChange(creditBookId: number) {
    if (creditBookId !== null) {
      const creditBook = creditBookState?.creditBooks?.find(creditBook => {
        return creditBook.id === creditBookId;
      });

      updateCreditBookState(prevState => ({
        ...prevState,
        selectedCreditBook: creditBook,
      }));
    }
  }

  return (
    <div>
      <div className="credit-book-header">
        <div className="portal-ha-balance-credit-container">
          <div className="portal-ha-left">
            <div className="portal-ha-left_icon_container">
              <div>
                <FontAwesomeIcon icon={faWallet} className="portal-ha-left_icon" />
              </div>
            </div>
            <div className="portal-ha-left_balance">
              <div>
                <p className="portal-ha-left_left_text">{"Current balance"}</p>
              </div>
              <div className="portal-ha-left_currency_text">
                <LocaleCurrency amount={creditState[0]?.balance} />
              </div>
            </div>
          </div>
        </div>

        <div className="credit-book-filters-container">
          <div className="credit-book-credit-select">
            <Select
              className=""
              defaultValue={creditBookState?.selectedCreditBook?.id}
              onChange={(id: number) => handleCreditBookChange(id)}
              placeholder={t("Select credit book")}
            >
              {creditBookState?.creditBooks?.map(book => {
                return (
                  <Option key={book.id} value={book.id} name={book.title}>
                    <span>{book.title}</span>
                  </Option>
                );
              })}
            </Select>
          </div>

          <div className="portal-credit-book-filters-container">
            <div className="w-full">
              <DatePickerInput
                months={windowSize.width > TABLET_WIDTH ? 2 : 1}
                position="right"
                startingDate={filterState.dateRangeStart}
                setStartingDate={setDateRangeStart}
                endingDate={filterState.dateRangeEnd}
                setEndingDate={setDateRangeEnd}
                showQuickOptions={windowSize.width > TABLET_WIDTH}
              />
            </div>
            <div className="credit-book-filters-container_accordion">
              <div>
                <CustomerFilterAccordion
                  activeFilters={activeFilters}
                  applyAllAction={updatedFilters => {
                    setActiveFilters(updatedFilters);
                  }}
                  clearAllAction={updatedFilters => {
                    if (updatedFilters.length === 0) {
                      return undefined;
                    }
                    setActiveFilters(updatedFilters);
                  }}
                  allFiltersAreApplied={filterContextIsEqual(activeFilters, filters)}
                  rightAligned
                  isLoading={activeFilters === undefined}
                  paymentStatus={null}
                  paymentType={null}
                  priceMatch
                  creditCard={null}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <>
          <DataTable
            columns={[{ label: t("Date") }, { label: t("Amount") }, { label: t("Order #") }, { label: t("Kind") }]}
          >
            {creditTransactionState?.map((transaction: ICreditTransactionRes) => {
              return (
                <tr key={transaction?.id}>
                  <td>
                    {transaction?.processed_at_local ? moment(transaction?.processed_at_local).format("ll") : null}
                  </td>
                  <td>
                    {String(transaction?.currency).toUpperCase()} {displayCurrency("cad", transaction?.amount)}
                  </td>
                  <td>{transaction?.order_number}</td>
                  <td>
                    {transaction?.kind === "debit_transfer" || transaction?.kind === "debit"
                      ? transactionBadge("debit_adjustment", t)
                      : transactionBadge("credit_adjustment", t)}
                  </td>
                </tr>
              );
            })}
          </DataTable>
        </>
      </div>
    </div>
  );
}
